/**
 * Pure search-text + query-match helpers for collection surfaces (CB-4).
 *
 * Generalized from ui/template-search.js's `templateMatchesQuery` (an OR over
 * six hardcoded fields) into a two-step contract every surface can share: the
 * adapter builds one lowercase haystack per record ONCE (`buildSearchText`),
 * and typing runs token matches against it (`matchesQuery`).
 *
 * `highlightRanges` returns index pairs AS DATA, never HTML. Palette's
 * `highlightMatch` regex-replaces `<mark>` into an already-escaped string —
 * an escaping hazard the shared layer must not inherit; the DOM layer builds
 * mark nodes over a text node from these ranges instead (CB-5a).
 */

/**
 * One lowercase haystack from a record's searchable fields. Nullish and empty
 * values drop out; everything else is stringified.
 * @param {Array<unknown>} fields
 * @returns {string}
 */
export function buildSearchText(fields) {
  if (!Array.isArray(fields)) return '';
  return fields
    .filter((f) => f !== null && f !== undefined && String(f).trim() !== '')
    .map((f) => String(f).toLowerCase())
    .join(' ');
}

/**
 * Whitespace-split query tokens, lowercased. Empty/blank queries tokenize to
 * an empty array (which `matchesQuery` treats as match-everything).
 * @param {string} query
 * @returns {string[]}
 */
export function tokenizeQuery(query) {
  return String(query ?? '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * Every token must appear somewhere in the haystack (AND semantics — a
 * one-token query behaves exactly like the donor's single substring test).
 * @param {string} searchText — a `buildSearchText` haystack
 * @param {string|string[]} query — raw query string, or pre-tokenized
 * @returns {boolean}
 */
export function matchesQuery(searchText, query) {
  const tokens = Array.isArray(query) ? query : tokenizeQuery(query);
  if (tokens.length === 0) return true;
  const haystack = String(searchText ?? '').toLowerCase();
  return tokens.every((t) => haystack.includes(t));
}

/**
 * Where the query matches inside a display label, as merged, ordered,
 * half-open [start, end) index pairs over the ORIGINAL label string.
 * Case-insensitive; every occurrence of every token; overlapping or adjacent
 * hits merge into one range so the DOM layer never nests marks.
 * @param {string} label
 * @param {string} query
 * @returns {Array<[number, number]>}
 */
export function highlightRanges(label, query) {
  const text = String(label ?? '');
  const tokens = tokenizeQuery(query);
  if (!text || tokens.length === 0) return [];
  const lower = text.toLowerCase();
  const raw = [];
  for (const token of tokens) {
    let from = 0;
    for (;;) {
      const at = lower.indexOf(token, from);
      if (at === -1) break;
      raw.push([at, at + token.length]);
      from = at + 1;
    }
  }
  if (raw.length === 0) return [];
  raw.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged = [raw[0].slice()];
  for (let i = 1; i < raw.length; i++) {
    const last = merged[merged.length - 1];
    if (raw[i][0] <= last[1]) last[1] = Math.max(last[1], raw[i][1]);
    else merged.push(raw[i].slice());
  }
  return merged;
}
